import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "stock-reservations";

export type ComponentRole = "product" | "engine" | "alternator";

export interface IncomingShipment {
	loadReportId: string;
	estimatedArrivalDate: string;
	arrivalPort: string | null;
	status: string;
	quantity: number;
	reserved: number;
	free: number;
}

export interface OwnReservation {
	id: string;
	lineStockItemId: string;
	componentRole: ComponentRole;
	alternativeGroup: number;
	quantity: number;
	source: "stock" | "incoming";
	loadReportId: string | null;
	expiresAt: string;
}

export interface ComponentAvailability {
	role: ComponentRole;
	stockItemId: string;
	code: string;
	name: string;
	uom: string | null;
	onHand: number;
	sapCommitted: number;
	crmReserved: number;
	crmReservedOffers: number;
	free: number;
	incoming: IncomingShipment[];
	incomingTotal: number;
	incomingFree: number;
	ownReservations: OwnReservation[];
}

export interface LineAvailability {
	lineStockItemId: string;
	components: ComponentAvailability[];
}

export function useLineStockAvailability(stockItemIds: string[], offerId: string | undefined) {
	const ids = [...new Set(stockItemIds.filter(Boolean))].sort();
	return useQuery({
		queryKey: [KEY, "availability", ids, offerId ?? null],
		enabled: ids.length > 0,
		staleTime: 30_000,
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("stockItemIds", ids.join(","));
			if (offerId) params.set("offerId", offerId);
			return api.get<{ lines: LineAvailability[] }>(
				`/api/stock-reservations/availability?${params.toString()}`,
			);
		},
	});
}

export interface CreateReservationInput {
	offerId: string;
	lineStockItemId: string;
	stockItemId: string;
	alternativeGroup: number;
	quantity: number;
	source: "stock" | "incoming";
	loadReportId?: string;
}

export function useCreateStockReservation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateReservationInput) => api.post("/api/stock-reservations", input),
		onSettled: () => qc.invalidateQueries({ queryKey: [KEY] }),
	});
}

export function useReleaseStockReservation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete(`/api/stock-reservations/${id}`),
		onSettled: () => qc.invalidateQueries({ queryKey: [KEY] }),
	});
}
